import React from "react";
import {
  Container,
  Typography,
  Paper,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Box,
} from "@mui/material";
import { PrivacyTip, Update } from "@mui/icons-material";

const PrivacyPolicy = () => {
  return (
    <Box sx={{ maxWidth: 800, margin: "0 auto", padding: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <PrivacyTip color="primary" sx={{ fontSize: 40, mr: 2 }} />
        <Typography variant="h3" component="h1">
          Privacy Policy
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" mb={4}>
        <Update color="action" sx={{ mr: 1 }} />
        <Typography variant="subtitle1" color="text.secondary">
          Last updated: 15/02/2025
        </Typography>
      </Box>

      <Typography paragraph>
        UK Company Incorporation ("we", "us", or "our") operates the
        ukcompanyincorporation.co.uk website (the "Service").
      </Typography>
      <Typography paragraph>
        This page informs you of our policies regarding the collection, use, and
        disclosure of personal data when you use our Service and the choices you
        have associated with that data.
      </Typography>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h4" component="h2" gutterBottom>
        Information Collection And Use
      </Typography>
      <Typography paragraph>
        We collect several different types of information for various purposes
        to provide and improve our Service to you.
      </Typography>

      <Typography variant="h5" component="h3" gutterBottom>
        Types of Data Collected
      </Typography>

      <Typography variant="h6" component="h4" gutterBottom>
        Personal Data
      </Typography>
      <Typography paragraph>
        While using our Service, we may ask you to provide us with certain
        personally identifiable information that can be used to contact or
        identify you ("Personal Data"). Personally identifiable information may
        include, but is not limited to:
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText primary="Email address" />
        </ListItem>
        <ListItem>
          <ListItemText primary="First name and last name" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Phone number" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Address, Postal code, City" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Cookies and Usage Data" />
        </ListItem>
      </List>

      <Typography variant="h6" component="h4" gutterBottom>
        Usage Data
      </Typography>
      <Typography paragraph>
        We may also collect information how the Service is accessed and used
        ("Usage Data"). This Usage Data may include information such as your
        computer's Internet Protocol address (e.g. IP address), browser type,
        browser version, the pages of our Service that you visit, the time and
        date of your visit, the time spent on those pages, unique device
        identifiers and other diagnostic data.
      </Typography>

      <Typography variant="h6" component="h4" gutterBottom>
        Tracking & Cookies Data
      </Typography>
      <Typography paragraph>
        We use cookies and similar tracking technologies to track the activity
        on our Service and hold certain information.
      </Typography>
      <Typography paragraph>
        Cookies are files with small amount of data which may include an
        anonymous unique identifier. Cookies are sent to your browser from a
        website and stored on your device. Tracking technologies also used are
        beacons, tags, and scripts to collect and track information and to
        improve and analyze our Service.
      </Typography>
      <Typography paragraph>
        You can instruct your browser to refuse all cookies or to indicate when
        a cookie is being sent. However, if you do not accept cookies, you may
        not be able to use some portions of our Service.
      </Typography>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h4" component="h2" gutterBottom>
        Use of Data
      </Typography>
      <Typography paragraph>
        UK Company Incorporation uses the collected data for various purposes:
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText primary="To provide and maintain our Service" />
        </ListItem>
        <ListItem>
          <ListItemText primary="To notify you about changes to our Service" />
        </ListItem>
        <ListItem>
          <ListItemText primary="To allow you to participate in interactive features of our Service when you choose to do so" />
        </ListItem>
        <ListItem>
          <ListItemText primary="To provide customer support" />
        </ListItem>
        <ListItem>
          <ListItemText primary="To gather analysis or valuable information so that we can improve our Service" />
        </ListItem>
        <ListItem>
          <ListItemText primary="To monitor the usage of our Service" />
        </ListItem>
        <ListItem>
          <ListItemText primary="To detect, prevent and address technical issues" />
        </ListItem>
      </List>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h4" component="h2" gutterBottom>
        Legal Basis for Processing Personal Data Under General Data Protection
        Regulation (GDPR)
      </Typography>
      <Typography paragraph>
        If you are from the European Economic Area (EEA), UK Company
        Incorporation's legal basis for collecting and using the personal
        information described in this Privacy Policy depends on the Personal
        Data we collect and the specific context in which we collect it.
      </Typography>
      <Typography paragraph>
        UK Company Incorporation may process your Personal Data because:
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText primary="We need to perform a contract with you" />
        </ListItem>
        <ListItem>
          <ListItemText primary="You have given us permission to do so" />
        </ListItem>
        <ListItem>
          <ListItemText primary="The processing is in our legitimate interests and it's not overridden by your rights" />
        </ListItem>
        <ListItem>
          <ListItemText primary="For payment processing purposes" />
        </ListItem>
        <ListItem>
          <ListItemText primary="To comply with the law" />
        </ListItem>
      </List>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h4" component="h2" gutterBottom>
        Your Data Protection Rights Under General Data Protection Regulation
        (GDPR)
      </Typography>
      <Typography paragraph>
        If you are a resident of the European Economic Area (EEA), you have
        certain data protection rights. UK Company Incorporation aims to take
        reasonable steps to allow you to correct, amend, delete, or limit the
        use of your Personal Data.
      </Typography>
      <Typography paragraph>
        If you wish to be informed what Personal Data we hold about you and if
        you want it to be removed from our systems, please contact us.
      </Typography>
      <Typography paragraph>
        In certain circumstances, you have the following data protection rights:
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText
            primary="The right to access, update or to delete the information we have on you"
            secondary="Whenever made possible, you can access, update or request deletion of your Personal Data directly within your account settings section."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="The right of rectification"
            secondary="You have the right to have your information rectified if that information is inaccurate or incomplete."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="The right to object"
            secondary="You have the right to object to our processing of your Personal Data."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="The right of restriction"
            secondary="You have the right to request that we restrict the processing of your personal information."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="The right to data portability"
            secondary="You have the right to be provided with a copy of the information we have on you in a structured, machine-readable and commonly used format."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="The right to withdraw consent"
            secondary="You also have the right to withdraw your consent at any time where UK Company Incorporation relied on your consent to process your personal information."
          />
        </ListItem>
      </List>
      <Typography paragraph>
        Please note that we may ask you to verify your identity before
        responding to such requests.
      </Typography>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h4" component="h2" gutterBottom>
        Contact Us
      </Typography>
      <Typography paragraph>
        If you have any questions about this Privacy Policy, please contact us:
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText primary="By email: privacy@ukcompanyincorporation.co.uk" />
        </ListItem>
        <ListItem>
          <ListItemText primary="By visiting this page on our website: ukcompanyincorporation.co.uk/contact" />
        </ListItem>
        <ListItem>
          <ListItemText primary="By phone: [Insert Phone Number]" />
        </ListItem>
      </List>
    </Box>
  );
};

export default PrivacyPolicy;
